"use client";

import dynamic from "next/dynamic";
import EventMultiOutcomeChart from "@/app/components/EventMultiOutcomeChart";
import PolymarketCryptoHeroChart from "@/app/market/[slug]/components/PolymarketCryptoHeroChart";
import CryptoPriceLevelChart from "./CryptoPriceLevelChart";
import { extractStrike } from "@/lib/kalshi-crypto";
import { isCryptoUpDownTitle, cryptoIntervalFromWindow } from "@/lib/crypto-updown";
import EventHero from "./EventHero";
import EventSeriesPills from "./EventSeriesPills";
import KalshiSportsHero from "./KalshiSportsHero";

const HyperliquidMarketChart = dynamic(
  () => import("@/app/market/[slug]/components/HyperliquidMarketChart"),
  { ssr: false },
);
const HyperliquidCryptoChart = dynamic(
  () => import("@/app/components/HyperliquidCryptoChart"),
  {
    ssr: false,
    // Skeleton with the exact same vertical footprint as the loaded
    // chart so the rest of the page (markets grid, sidebar offsets,
    // sticky elements) doesn't shift when the dynamic chunk arrives.
    // Heights match the loaded layout: ~44 px header pill row + ~340 px
    // chart canvas. Selector row is intentionally NOT reserved — PM
    // callers hide it (`hideTimeframeSelector`), and on HL the selector
    // appears below the chart so its absence is invisible vs the
    // skeleton.
    loading: () => (
      <div className="bg-transparent">
        <div className="flex items-center justify-between gap-3 px-3 sm:px-4 pt-3 pb-2">
          <div className="flex items-center gap-2 min-w-0">
            <div className="h-7 w-20 rounded bg-gray-100 dark:bg-white/5 animate-pulse" />
            <div className="h-5 w-32 rounded bg-gray-100 dark:bg-white/5 animate-pulse" />
          </div>
          <div className="h-7 w-24 rounded bg-red-500/10 animate-pulse" />
        </div>
        <div className="h-[340px] w-full px-3 sm:px-4 pb-3">
          <div className="h-full w-full rounded-[5px] bg-gray-50 dark:bg-white/[0.02] animate-pulse" />
        </div>
      </div>
    ),
  },
);
const KalshiCryptoCandlestickChart = dynamic(
  () => import("@/app/components/KalshiCryptoCandlestickChart"),
  { ssr: false },
);
// Predict crypto up/down ("Bitcoin Up or Down …"): the same Binance-backed
// asset-price chart Polymarket uses. Predict's public GraphQL doesn't expose
// the strike/priceFeedId (they live on its internal API), so CryptoPriceChart
// derives the strike from the open price at eventStartTime and streams the
// live price. ssr:false — depends on browser-only realtime hooks.
const CryptoPriceChart = dynamic(
  () => import("@/app/market/[slug]/components/CryptoPriceChart"),
  { ssr: false },
);

// Symbol → Binance spot pair for the candle chart (BTC/USDT, ETH/USDT,
// …). HyperliquidCryptoChart hits api.binance.com/api/v3/klines — the
// spot feed, matching Polymarket's resolution oracle for the recurring
// "Bitcoin above ___ on May Y?" series (1-minute spot BTC/USDT 12:00 ET
// close). HL surface uses the same map for consistency. Lives at module
// scope so the object isn't reallocated on every render.
const HL_TO_BINANCE: Record<string, string> = {
  BTC: "BTCUSDT",
  ETH: "ETHUSDT",
  SOL: "SOLUSDT",
  BNB: "BNBUSDT",
  XRP: "XRPUSDT",
  DOGE: "DOGEUSDT",
  HYPE: "HYPEUSDT",
};

type Source = "polymarket" | "kalshi" | "predict" | "hyperliquid";

export interface EventHeroChartProps {
  source: Source;
  isSportsMatchView: boolean;
  resolvedEventData: any;
  eventData: any;
  slug: string;
  formatVolume: (n: any) => string;
  isActive: boolean;
  activeMarketsCount: number;

  // Polymarket crypto-market hero (single-market crypto pages routed
  // through the event slug — e.g. "will-bitcoin-hit-90k-jan-5").
  isPolymarketCryptoMarketEvent: boolean;
  effectiveResolvedPolymarketCryptoMarket: any;
  resolvedPolymarketCryptoHeroData: any;
  resolvedPolymarketCryptoMarketEnded: boolean;
  selectedMarketLooksLikePolymarketCrypto: boolean;

  // Chart selection inputs.
  cryptoInfo: any;
  cryptoCloseDate: string | null;
  switchToNextCryptoEvent: () => void;
  currentCryptoPrice: number | null;
  s3Open6H: number | null;
  kalshiCryptoTargetLabel: string | null;
  pmPriceLevelInfo: any;
  isDeadlineEvent: boolean;
  hlPriceLevelInfo: any;
  selectedMarket: any;
  chartMarkets: any[];
  dflowPrices: any;
  isEventLive: boolean;
  /** Pre-fetched 30m candles from page.tsx for the PM recurring-crypto
   *  chart. Threaded through to PolymarketPriceLevelBlock so the
   *  underlying HyperliquidCryptoChart can seed its state and paint
   *  with data on the first frame. Null when the event isn't a
   *  recognised crypto series. */
  initialCryptoCandles?: any[] | null;
}

/**
 * Hero + chart card for the /event/[slug] page. Picks the right hero
 * (KalshiSportsHero / PolymarketCryptoHeroChart / EventHero) and the
 * right chart (Kalshi candles / PM price-level / HL candles / multi-
 * outcome) based on source and event shape. Pure presentational —
 * data wiring stays in EventClient. The outer parallax `<div>` also
 * stays in EventClient because it spans more than just this card.
 */
export function EventHeroChart({
  source,
  isSportsMatchView,
  resolvedEventData,
  eventData,
  slug,
  formatVolume,
  isActive,
  activeMarketsCount,
  isPolymarketCryptoMarketEvent,
  effectiveResolvedPolymarketCryptoMarket,
  resolvedPolymarketCryptoHeroData,
  resolvedPolymarketCryptoMarketEnded,
  selectedMarketLooksLikePolymarketCrypto,
  cryptoInfo,
  cryptoCloseDate,
  switchToNextCryptoEvent,
  currentCryptoPrice,
  s3Open6H,
  kalshiCryptoTargetLabel,
  pmPriceLevelInfo,
  isDeadlineEvent,
  hlPriceLevelInfo,
  selectedMarket,
  chartMarkets,
  dflowPrices,
  isEventLive,
  initialCryptoCandles,
}: EventHeroChartProps) {
  if (source === "kalshi" && isSportsMatchView) {
    return (
      <KalshiSportsHero
        eventData={resolvedEventData}
        slug={slug}
        formatVolume={formatVolume}
        competition={(eventData as any)?.competition}
        startDate={(eventData as any)?.startDate}
      />
    );
  }

  if (
    isPolymarketCryptoMarketEvent &&
    effectiveResolvedPolymarketCryptoMarket &&
    resolvedPolymarketCryptoHeroData
  ) {
    return (
      <PolymarketCryptoHeroChart
        heroData={resolvedPolymarketCryptoHeroData}
        marketData={effectiveResolvedPolymarketCryptoMarket}
        marketEnded={resolvedPolymarketCryptoMarketEnded}
        showClosedBanner={false}
        heroPaddingClass="px-3 pt-3 pb-0"
      />
    );
  }

  if (selectedMarketLooksLikePolymarketCrypto && !effectiveResolvedPolymarketCryptoMarket) {
    return (
      <div className="p-4 md:p-5">
        <div className="animate-pulse space-y-4">
          <div className="flex items-start gap-4">
            <div className="h-[70px] w-[70px] rounded-[5px] bg-gray-100 dark:bg-[#111]" />
            <div className="flex-1 space-y-2">
              <div className="h-4 w-28 rounded bg-gray-100 dark:bg-[#111]" />
              <div className="h-6 w-4/5 rounded bg-gray-100 dark:bg-[#111]" />
              <div className="flex gap-2">
                <div className="h-5 w-16 rounded bg-gray-100 dark:bg-[#111]" />
                <div className="h-5 w-16 rounded bg-gray-100 dark:bg-[#111]" />
                <div className="h-5 w-16 rounded bg-gray-100 dark:bg-[#111]" />
              </div>
            </div>
          </div>
          <div className="h-[320px] rounded-[5px] bg-gray-100 dark:bg-[#111]" />
        </div>
      </div>
    );
  }

  // Series-pills navigator (Past ▾ / May 28 ● / May 29 / … / More ▾).
  // Only renders when the event belongs to a Polymarket recurring series
  // — the component itself bails on empty data so wiring it up
  // unconditionally is safe. Sits between the hero and the chart on the
  // current event slug. PM source only; HL/Kalshi/Predict events use
  // their own grouping concepts.
  const seriesId: string | null =
    source === "polymarket"
      ? (Array.isArray((resolvedEventData as any)?.series)
          ? ((resolvedEventData as any).series[0]?.id || null)
          : null)
      : null;

  // Predict crypto up/down → asset price chart (see CryptoPriceChart import).
  // startDate comes from the predict event payload (raw.startsAt); endDate from
  // the market/event. interval is derived from the window so the live tape
  // sizes correctly. Null when not a predict crypto up/down market.
  const predictCryptoUpDown = (() => {
    if (source !== "predict") return null;
    const question = selectedMarket?.question || eventData?.title || "";
    // Prefer the backend's authoritative flag (Predict variantData
    // marketVariant === CRYPTO_UP_DOWN); fall back to the title heuristic.
    const isCrypto = (eventData as any)?.cryptoUpDown === true || isCryptoUpDownTitle(question);
    if (!isCrypto) return null;
    const eventStartTime = (eventData as any)?.startDate || null;
    const endDate = selectedMarket?.endDate || eventData?.endDate || null;
    // Exact strike from Predict (variantData.startPrice). null → CryptoPriceChart
    // derives it from the Binance open at eventStartTime (Predict's own
    // resolution source is Binance, so the line still matches).
    const priceToBeat = typeof (eventData as any)?.priceToBeat === "number"
      ? (eventData as any).priceToBeat
      : null;
    // Full candle duration so the chart shows the whole price path since the
    // open (like predict.fun), not just the last 30s rolling tape.
    const windowSeconds = eventStartTime && endDate
      ? Math.max(60, Math.round((new Date(endDate).getTime() - new Date(eventStartTime).getTime()) / 1000))
      : undefined;
    return {
      question,
      eventStartTime,
      endDate,
      priceToBeat,
      interval: cryptoIntervalFromWindow(eventStartTime, endDate),
      marketSlug: selectedMarket?.slug || slug,
      windowSeconds,
    };
  })();

  return (
    <>
      <EventHero
        eventData={resolvedEventData}
        isActive={isActive}
        source={source}
        activeMarketsCount={activeMarketsCount}
        formatVolume={formatVolume}
        slug={slug}
        isCryptoLive={cryptoInfo.isCrypto && isActive}
      />

      {seriesId && (
        <EventSeriesPills seriesId={String(seriesId)} currentSlug={slug} />
      )}

      {predictCryptoUpDown ? (
        <div className="border-t border-gray-200 dark:border-[#1A1A1A]">
          <CryptoPriceChart
            priceToBeat={predictCryptoUpDown.priceToBeat}
            eventStartTime={predictCryptoUpDown.eventStartTime}
            endDate={predictCryptoUpDown.endDate}
            question={predictCryptoUpDown.question}
            conditionId={null}
            marketSlug={predictCryptoUpDown.marketSlug}
            interval={predictCryptoUpDown.interval}
            preferredSource="binance"
            windowSeconds={predictCryptoUpDown.windowSeconds}
          />
        </div>
      ) : cryptoInfo.isCrypto ? (
        <div className="border-t border-gray-200 dark:border-[#1A1A1A]">
          <KalshiCryptoCandlestickChart
            symbol={cryptoInfo.symbol}
            s3Prefix={cryptoInfo.s3Prefix}
            closeDate={cryptoCloseDate}
            onMarketClosed={switchToNextCryptoEvent}
            fallbackPrice={currentCryptoPrice}
            fallbackOpen6H={s3Open6H}
            targetLabel={kalshiCryptoTargetLabel}
          />
        </div>
      ) : pmPriceLevelInfo.isPriceLevel ? (
        <PolymarketPriceLevelBlock
          pmPriceLevelInfo={pmPriceLevelInfo}
          endDate={resolvedEventData.endDate || null}
          startDate={(resolvedEventData as any).startDate || null}
          initialCandles={initialCryptoCandles ?? undefined}
        />
      ) : isDeadlineEvent ? (
        // Deadline series: chart hidden — overlapping cumulative-deadline
        // timelines read as noise; PM hides it too. Per-outcome chart is
        // still reachable from the outcome detail panel.
        null
      ) : source === "hyperliquid" ? (
        <div className="border-t border-gray-200 dark:border-[#1A1A1A]">
          <HyperliquidChartBlock
            slug={slug}
            hlPriceLevelInfo={hlPriceLevelInfo}
            selectedMarketQuestion={selectedMarket?.question || ""}
            selectedOutcomes={(selectedMarket as any)?.outcomes}
          />
        </div>
      ) : chartMarkets.length >= 1 ? (
        <div className="border-t border-gray-200 dark:border-[#1A1A1A]">
          <EventMultiOutcomeChart
            markets={chartMarkets}
            eventTitle={eventData.title}
            source={source}
            eventSlug={slug}
            realtimePrices={source === "kalshi" ? dflowPrices : undefined}
            defaultTimeFilter={isEventLive ? "1H" : isSportsMatchView ? "1D" : undefined}
            eventVolume={eventData.volume}
            eventEndDate={eventData.endDate}
            hasHoldingRewards={
              source === "polymarket" &&
              eventData.markets?.some((m: any) => m?.holdingRewardsEnabled === true)
            }
          />
        </div>
      ) : null}
    </>
  );
}

/**
 * HIP-4 crypto events: lightweight-charts candlestick view with N strike
 * lines drawn directly on the chart — one for binary events, all
 * thresholds for priceBucket events with the SELECTED bucket's boundary
 * highlighted. Falls back to HyperliquidMarketChart for non-price HL
 * questions or symbols outside HL_TO_BINANCE.
 */
function HyperliquidChartBlock({
  slug,
  hlPriceLevelInfo,
  selectedMarketQuestion,
  selectedOutcomes,
}: {
  slug: string;
  hlPriceLevelInfo: any;
  selectedMarketQuestion: string;
  selectedOutcomes?: any[];
}) {
  // Named 2-way markets (PSG / Arsenal) draw both outcome lines; plain
  // Yes/No binaries keep the single YES line. The chart decides based on
  // whether the labels are real names.
  const yesLabel = typeof selectedOutcomes?.[0] === "string" ? selectedOutcomes[0] : (selectedOutcomes?.[0] as any)?.name;
  const noLabel = typeof selectedOutcomes?.[1] === "string" ? selectedOutcomes[1] : (selectedOutcomes?.[1] as any)?.name;
  if (!hlPriceLevelInfo.isPriceLevel) {
    return <HyperliquidMarketChart marketSlug={slug} yesLabel={yesLabel} noLabel={noLabel} />;
  }
  const binanceSymbol = HL_TO_BINANCE[hlPriceLevelInfo.crypto.symbol];
  if (!binanceSymbol) {
    return <HyperliquidMarketChart marketSlug={slug} yesLabel={yesLabel} noLabel={noLabel} />;
  }
  // Highlight the threshold matching the SELECTED bucket
  // ("BTC < $79,303" → 79303 emphasised), so the user can see which
  // boundary they're betting against without losing the rest of the
  // ladder.
  const selectedStrike = extractStrike(selectedMarketQuestion);
  const lines = hlPriceLevelInfo.strikes.map((s: any) => ({
    price: s.price,
    label: s.label,
    emphasis: selectedStrike != null && Math.abs(s.price - selectedStrike) < 0.01,
  }));
  return (
    <HyperliquidCryptoChart
      symbol={hlPriceLevelInfo.crypto.symbol}
      binanceSymbol={binanceSymbol}
      thresholds={lines}
      expiryISO={hlPriceLevelInfo.expiryISO}
    />
  );
}

/**
 * Polymarket recurring crypto / equity price-level events ("Bitcoin above
 * $X on May Y?", "Will Tesla close above $250?"). Routes crypto events
 * (BTC/ETH/SOL/BNB/XRP/DOGE/HYPE) to the same TradingView-style candle
 * chart used for HL — the area chart felt out of place next to PM's own
 * UI which is candles-with-strikes. Equities + symbols outside the
 * Binance map keep the existing area chart (Pyth live ticks; no Binance
 * OHLC endpoint for equities). Strike lines are non-emphasised — there
 * is no "selected bucket" on PM since each sub-market is its own outcome
 * the user trades independently.
 */
function PolymarketPriceLevelBlock({
  pmPriceLevelInfo,
  endDate,
  startDate,
  initialCandles,
}: {
  pmPriceLevelInfo: any;
  endDate: string | null;
  startDate: string | null;
  /** SSR-fetched 30m candles passed down from page.tsx so the chart
   *  paints with data on first frame. Undefined on legacy callers /
   *  non-crypto events — the chart falls back to a client fetch. */
  initialCandles?: any[];
}) {
  const symbol = pmPriceLevelInfo.crypto.symbol as string;
  const binanceSymbol = HL_TO_BINANCE[symbol];
  const isCrypto = pmPriceLevelInfo.assetClass === "crypto" && !!binanceSymbol;

  if (isCrypto) {
    // PM intentionally renders no strike overlay on these charts — the
    // candles speak for themselves and the strike pills already sit in
    // the markets grid below. Pass an empty thresholds array so the
    // shared HL component skips the IPriceLine draw entirely. The 30m
    // timeframe + hidden selector mirror Polymarket's reference UI.
    return (
      <HyperliquidCryptoChart
        symbol={symbol}
        binanceSymbol={binanceSymbol}
        thresholds={[]}
        expiryISO={endDate}
        defaultTimeframe="30m"
        hideTimeframeSelector
        interactive={false}
        initialCandles={initialCandles}
      />
    );
  }

  return (
    <CryptoPriceLevelChart
      symbol={symbol}
      polymarketSymbol={pmPriceLevelInfo.crypto.polymarketSymbol}
      strikes={pmPriceLevelInfo.strikes}
      endDate={endDate}
      eventStartTime={startDate}
      assetClass={pmPriceLevelInfo.assetClass}
    />
  );
}
